/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, InlineNotification, breakpoints } from '@coursera/cds-core';
import { SpinnerIcon } from '@coursera/cds-icons';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import { BlueStarSvg } from 'bundles/skills-common';

import _t from 'i18n!nls/skillset-learner-page';

import SkillSetTitleCard from './subcomponents/SkillSetTitleCard';
import type { CMLDescription, ThirdPartyOrganization } from './subcomponents/SkillSetTitleCard';
import SkillsInformationSection, {
  SkillsInformationSectionPlaceholder,
} from './subcomponents/SkillsInformationSection';

export type SkillProficency = {
  skillId: string;
  skillName: string;
  targetProficiency: number;
  score: number;
};

export type SkillPercentile = {
  skillId: string;
  percentile: number;
};

type Props = {
  name: string;
  description: CMLDescription;
  skills: SkillProficency[];
  breadcrumb: JSX.Element;

  onSaveSkillSet: () => Promise<void>;
  isSkillSetSaved: boolean;
  thirdPartyOrganization: ThirdPartyOrganization;
  skillPercentile: SkillPercentile[];

  occupationName: string;
};

type SaveButtonProps = {
  initialIsSkillSetSaved: boolean;
  onToggleSave: () => void;
  color: 'invert' | 'default';
};

const styles = {
  buttonChangeA11yAnnouncer: css`
    height: 1px;
    width: 1px;
    position: fixed;
    top: -1;
    left: -1;
    overflow: hidden;
  `,
  buttonContainer: css`
    ${breakpoints.down('sm')} {
      button {
        min-width: 120px;
      }
    }
  `,
};

const SaveSkillSetButton: React.FC<SaveButtonProps> = ({ initialIsSkillSetSaved, onToggleSave, color }) => {
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showError, setShowError] = useState(false);
  const [hasButtonBeenPressed, setHasButtonBeenPressed] = useState(false);
  const [isSaved, setIsSaved] = useState(initialIsSkillSetSaved);

  const tooltipText = _t(
    'Saving a SkillSet will personalize your course recommendations and add it to the My Courses tab.'
  );

  const getButtonText = () => {
    if (saveInProgress && isSaved) {
      return _t('Removing SkillSet');
    } else if (saveInProgress && !isSaved) {
      return _t('Saving SkillSet');
    } else if (!saveInProgress && isSaved) {
      return _t('Remove from saved');
    }
    return _t('Save SkillSet');
  };

  const getAnnouncementForScreenReader = () => {
    if (!hasButtonBeenPressed || showError) {
      return '';
    }
    if (!isSaved && saveInProgress) {
      return _t('Saving SkillSet');
    }
    if (isSaved && saveInProgress) {
      return _t('Removing SkillSet');
    }
    if (isSaved && !saveInProgress) {
      return _t('SkillSet saved');
    }
    if (!isSaved && !saveInProgress) {
      return _t('SkillSet removed');
    }
    return '';
  };

  return (
    <TooltipWrapper
      tooltipClassName="ProgramSkillObjectivesPage-SaveSkillSetTooltip"
      message={<div id="save-skillset-tooltip">{tooltipText}</div>}
      placement="bottom"
    >
      <div css={styles.buttonContainer}>
        <Button
          variant={color === 'invert' ? 'ghostInvert' : 'ghost'}
          size="small"
          iconPosition="before"
          icon={
            saveInProgress ? (
              <SpinnerIcon />
            ) : (
              <BlueStarSvg filled={isSaved} color={color === 'invert' ? undefined : 'currentColor'} />
            )
          }
          onClick={async () => {
            setHasButtonBeenPressed(true);
            setSaveInProgress(true);
            try {
              await onToggleSave();
              setIsSaved((prevState) => !prevState);
            } catch (ex) {
              setShowError(true);
            } finally {
              setSaveInProgress(false);
            }
          }}
          aria-describedby="save-skillset-tooltip-id"
          role="button"
          aria-label={`${getButtonText()}. ${tooltipText}`}
        >
          {/* This prevents the text change being announced in chrome by the screen reader */}
          <div aria-hidden>{getButtonText()}</div>
        </Button>
        {/* Allows us to decouple screen reader announcements from the button text so we can the user the save succeeded */}
        <div role="status" aria-live="polite" css={styles.buttonChangeA11yAnnouncer}>
          {getAnnouncementForScreenReader()}
        </div>
        {showError && (
          <InlineNotification severity="error" title={_t('Error')} onDismiss={() => setShowError(false)} role="alert">
            {_t('There was an error while saving. Please try again or contact an administrator.')}
          </InlineNotification>
        )}
      </div>
    </TooltipWrapper>
  );
};

const SkillSetSummaryCard: React.FC<Props> = (props) => {
  return (
    <section>
      <SkillSetTitleCard
        breadcrumb={props.breadcrumb}
        name={props.name}
        description={props.description}
        thirdPartyOrganization={props.thirdPartyOrganization}
        saveSkillsetButton={
          <SaveSkillSetButton
            initialIsSkillSetSaved={props.isSkillSetSaved}
            onToggleSave={props.onSaveSkillSet}
            color="invert"
          />
        }
      />
      <SkillsInformationSection
        occupationName={props.occupationName}
        skillPercentile={props.skillPercentile}
        skills={props.skills}
      />
    </section>
  );
};

type PlaceholderProps = {
  breadcrumb: JSX.Element;
};

const SkillSetSummaryCardPlaceholder: React.FC<PlaceholderProps> = ({ breadcrumb }) => {
  return (
    <section>
      <SkillSetTitleCard
        breadcrumb={breadcrumb}
        name=""
        description={null}
        thirdPartyOrganization={{
          name: '',
        }}
      />
      <SkillsInformationSectionPlaceholder />
    </section>
  );
};

export default SkillSetSummaryCard;
export { SkillSetSummaryCard, SkillSetSummaryCardPlaceholder, SaveSkillSetButton };
